/**
 * Trust POA&M hook (c15) — real persistence for the Roadmap/POA&M tab on
 * /trust/frameworks/$frameworkId (Trust bug #15).
 *
 * Replaces the prior mock generatePoam() + local-state-only Add-Item path,
 * which reverted on reload while toasting "POA&M item added". Items now persist
 * to the tenant-scoped poam_items table via /governance/frameworks/:id/poam.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { FrameworkId } from './mock-trust-data';
import { UI_TO_SERVER_FRAMEWORK_ID } from './use-framework-controls';

export type PoamStatus = 'open' | 'in_progress' | 'completed';

// Server VM (mirror of governance-frameworks.ts PoamItemSchema).
export interface ServerPoamItem {
  id: string;
  frameworkId: string;
  title: string;
  description: string;
  status: PoamStatus;
  ownerUserId: string | null;
  ownerName: string;
  controlCode: string | null;
  openDate: string;
  targetCloseDate: string;
  milestones: string[];
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  frameworkId: string;
  items: ServerPoamItem[];
}

// UI VM consumed by the RoadmapTab in $frameworkId.tsx.
export interface UiPoamItem {
  id: string;
  name: string;
  description: string;
  status: PoamStatus;
  owner: string;
  ownerUserId: string | null;
  controlCode: string | null;
  openDate: string;
  targetCloseDate: string;
  milestones: string[];
}

export function adaptPoam(s: ServerPoamItem): UiPoamItem {
  return {
    id: s.id,
    name: s.title,
    description: s.description,
    status: s.status,
    owner: s.ownerName,
    ownerUserId: s.ownerUserId,
    controlCode: s.controlCode,
    openDate: s.openDate,
    targetCloseDate: s.targetCloseDate,
    milestones: s.milestones,
  };
}

export interface PoamWritePayload {
  title: string;
  description: string;
  status: PoamStatus;
  ownerUserId?: string | null;
  ownerName: string;
  controlCode?: string | null;
  targetCloseDate: string; // ISO datetime
  milestones: string[];
}

function poamKey(serverId: string | undefined) {
  return ['trust', 'poam', serverId] as const;
}

export function useTrustPoam(uiFrameworkId: FrameworkId | undefined) {
  const serverId = uiFrameworkId
    ? UI_TO_SERVER_FRAMEWORK_ID[uiFrameworkId]
    : undefined;
  return useQuery<ListResponse, Error, UiPoamItem[]>({
    queryKey: poamKey(serverId),
    enabled: !!serverId && !!uiFrameworkId,
    queryFn: () =>
      apiClient.get<ListResponse>(`/governance/frameworks/${serverId}/poam`),
    select: (res) => res.items.map(adaptPoam),
  });
}

export function useCreatePoamItem(uiFrameworkId: FrameworkId | undefined) {
  const serverId = uiFrameworkId
    ? UI_TO_SERVER_FRAMEWORK_ID[uiFrameworkId]
    : undefined;
  const qc = useQueryClient();
  return useMutation<ServerPoamItem, Error, PoamWritePayload>({
    mutationFn: (body) =>
      apiClient.post<ServerPoamItem>(
        `/governance/frameworks/${serverId}/poam`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poamKey(serverId) });
    },
  });
}

export function useUpdatePoamItem(uiFrameworkId: FrameworkId | undefined) {
  const serverId = uiFrameworkId
    ? UI_TO_SERVER_FRAMEWORK_ID[uiFrameworkId]
    : undefined;
  const qc = useQueryClient();
  return useMutation<
    ServerPoamItem,
    Error,
    { id: string; patch: Partial<PoamWritePayload> }
  >({
    mutationFn: ({ id, patch }) =>
      apiClient.patch<ServerPoamItem>(
        `/governance/frameworks/${serverId}/poam/${id}`,
        patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poamKey(serverId) });
    },
  });
}

export function useDeletePoamItem(uiFrameworkId: FrameworkId | undefined) {
  const serverId = uiFrameworkId
    ? UI_TO_SERVER_FRAMEWORK_ID[uiFrameworkId]
    : undefined;
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ ok: boolean }>(
        `/governance/frameworks/${serverId}/poam/${id}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poamKey(serverId) });
    },
  });
}
